import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { AIReviewRepository } from "../../repositories/ai-review-repository.js";
import {
  AI_REVIEW_SUBJECT_LABELS,
  countFindings,
  deriveReviewStatus,
  evaluateSubmitGate,
  type AIAppliedChange,
  type AIReportReview,
  type AIReviewFinding,
  type AIReviewSubjectType,
  type AISubmitDecision
} from "../../domain/ai-review/ai-review-model.js";
import { AIProviderUnavailableError, type AIProvider } from "../../domain/ai-review/ai-provider.js";
import { validateFindingsOutput, type ProviderFinding } from "../../domain/ai-review/output-schema.js";
import { contentHash, runDeterministicValidation } from "../../domain/ai-review/report-validation-engine.js";
import { promptVersionOf } from "../../domain/ai-review/prompt-templates.js";
import { replaceAt } from "../../domain/ai-review/language-rules.js";
import { DEFAULT_AI_SETTINGS, normalizeAISettings, type AISettings } from "../../domain/ai-review/ai-settings.js";
import type { AIReviewSubject, AIReviewSubjectAdapter } from "./ai-review-adapters.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { localDayOf } from "../../shared/format/local-date.js";

const uuid = () => globalThis.crypto.randomUUID();

export interface AIReviewState {
  readonly subjectType: AIReviewSubjectType;
  readonly reportId: string;
  readonly reportNumber: string;
  readonly review: AIReportReview | null;
  readonly findings: AIReviewFinding[];
  readonly currentHash: string;
  /** O relatório mudou depois da revisão. */
  readonly stale: boolean;
  readonly editable: boolean;
  readonly gate: AISubmitDecision;
}

/**
 * AIReviewOrchestrator — o ÚNICO motor de revisão IA (substitui o
 * CortexOrchestrator). Todos os relatórios passam por aqui:
 *
 *   carregar (adaptador) → VALIDAÇÃO DETERMINÍSTICA → provedor (revisão) →
 *   validação de esquema → proteção de valores → findings → correções
 *   linguísticas seguras (se configurado) → nova revisão → auditoria
 *
 * Interno: NUNCA no /api/rpc (não confere permissão — quem confere é a
 * fachada `InspectIaReviewService`).
 */
export class AIReviewOrchestrator {
  private readonly adapters = new Map<AIReviewSubjectType, AIReviewSubjectAdapter>();

  constructor(
    private readonly repo: AIReviewRepository,
    private readonly providers: { local: AIProvider; external?: AIProvider | null },
    private readonly audit?: AuditRecorder,
    private readonly userName?: (userId: string) => Promise<string | null>
  ) {}

  registerAdapter(adapter: AIReviewSubjectAdapter): void {
    this.adapters.set(adapter.type, adapter);
  }

  adapterFor(type: AIReviewSubjectType): AIReviewSubjectAdapter {
    const a = this.adapters.get(type);
    if (!a) throw new AppError(ErrorCode.Validation, `No AI review adapter for ${type}`, { safeMessage: `A revisão IA ainda não está disponível para ${AI_REVIEW_SUBJECT_LABELS[type] ?? type}.` });
    return a;
  }

  supportedTypes(): AIReviewSubjectType[] {
    return [...this.adapters.keys()];
  }

  async settings(): Promise<AISettings> {
    return normalizeAISettings((await this.repo.loadSettings()) ?? DEFAULT_AI_SETTINGS);
  }

  /** O provedor da configuração. Externo configurado mas não integrado ⇒ indisponível (nunca cai calado no local). */
  private providerFor(settings: AISettings): AIProvider | null {
    return settings.provider === "EXTERNAL_HTTP" ? (this.providers.external ?? null) : this.providers.local;
  }

  async loadSubject(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReviewSubject> {
    const subject = await this.adapterFor(type).load(context, reportId);
    if (!subject) throw new AppError(ErrorCode.NotFound, "Report not found", { safeMessage: "Documento não encontrado." });
    return subject;
  }

  async state(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReviewState> {
    const subject = await this.loadSubject(context, type, reportId);
    const hash = contentHash(subject.fields, subject.facts);
    const review = await this.repo.latestReview(type, reportId);
    const findings = review ? await this.repo.listFindings(review.id) : [];
    const settings = await this.settings();
    const stale = !!review && review.status !== "AI_REVIEW_RUNNING" && review.status !== "AI_REVIEW_FAILED" && review.contentHash !== hash;
    const shown = review && stale ? { ...review, status: "AI_REVIEW_STALE" as const } : review;
    return {
      subjectType: type, reportId, reportNumber: subject.reportNumber, review: shown, findings, currentHash: hash, stale, editable: subject.editable,
      gate: evaluateSubmitGate(review, findings, hash, settings)
    };
  }

  /**
   * Cria a revisão (EM EXECUÇÃO, job QUEUED) e processa em seguida sem
   * segurar a chamada — a tela acompanha pelo `state`. `runReview` espera o
   * fim (usado pelo gate e pelos testes).
   */
  async requestReview(context: RequestContext, type: AIReviewSubjectType, reportId: string, opts: { canApplyLanguage: boolean }): Promise<AIReportReview> {
    const { review, done } = await this.start(context, type, reportId, opts, 0);
    void done.catch(() => undefined);
    return review;
  }

  async runReview(context: RequestContext, type: AIReviewSubjectType, reportId: string, opts: { canApplyLanguage: boolean }): Promise<AIReportReview> {
    const { done } = await this.start(context, type, reportId, opts, 0);
    return done;
  }

  private async start(context: RequestContext, type: AIReviewSubjectType, reportId: string, opts: { canApplyLanguage: boolean }, depth: number): Promise<{ review: AIReportReview; done: Promise<AIReportReview> }> {
    const subject = await this.loadSubject(context, type, reportId);
    const settings = await this.settings();
    const provider = this.providerFor(settings);
    const userId = String(context.userId || "system");
    const review: AIReportReview = {
      id: `AIR-${uuid()}`, subjectType: type, reportId, reportNumber: subject.reportNumber, reportRevision: subject.revision, clientId: subject.clientId,
      status: "AI_REVIEW_RUNNING", jobStatus: "QUEUED", contentHash: contentHash(subject.fields, subject.facts),
      technicalStatus: "OK", findingsCount: 0, criticalCount: 0, warningCount: 0, autoAppliedCount: 0,
      modelProvider: provider?.id ?? settings.provider, modelName: provider?.model ?? settings.model, modelVersion: provider?.modelVersion ?? "",
      promptVersion: `${promptVersionOf("REPORT_GRAMMAR_REVIEW")}+${promptVersionOf("REPORT_TECHNICAL_REVIEW")}`,
      failureReason: "", startedAt: new Date().toISOString(), completedAt: null, requestedBy: userId,
      requestedByName: (this.userName ? await this.userName(userId).catch(() => null) : null) ?? ""
    };
    await this.repo.saveReview(review);
    await this.record(context, AuditAction.AiReviewStarted, review, { subjectType: type, reportNumber: subject.reportNumber });
    return { review: { ...review }, done: this.process(context, review, subject, settings, provider, opts, depth) };
  }

  private async process(context: RequestContext, review: AIReportReview, subject: AIReviewSubject, settings: AISettings, provider: AIProvider | null, opts: { canApplyLanguage: boolean }, depth: number): Promise<AIReportReview> {
    review.jobStatus = "PROCESSING";
    await this.repo.saveReview(review);
    const previous = await this.previousFindings(review);
    try {
      // 1. Regras objetivas ANTES da IA.
      const deterministic = runDeterministicValidation({ fields: subject.fields, facts: subject.facts, modulePendencies: subject.modulePendencies, today: localDayOf(new Date()) });
      // 2. Provedor (saída não confiável até passar pelo esquema).
      if (!provider) throw new AIProviderUnavailableError("Provedor externo configurado, mas NÃO INTEGRADO nesta instalação.");
      if (!(await provider.isAvailable())) throw new AIProviderUnavailableError();
      const knowledgeDocs = settings.knowledgeBaseEnabled ? (await this.repo.listKnowledge(subject.clientId)).filter(k => k.status === "ACTIVE").map(k => k.documentCode) : [];
      const known = new Set(subject.fields.map(f => f.fieldPath));
      let parsed: ProviderFinding[] | null = null;
      let lastError = "";
      for (let attempt = 0; attempt <= settings.retries && !parsed; attempt++) {
        const raw = await withTimeout(provider.review({ subjectLabel: AI_REVIEW_SUBJECT_LABELS[subject.type], fields: subject.fields, facts: subject.facts, knowledgeDocuments: knowledgeDocs, promptVersion: review.promptVersion }), settings.timeoutMs);
        const res = validateFindingsOutput(raw, known);
        if (res.ok) parsed = res.findings; else lastError = res.error;
      }
      if (!parsed) throw new Error(`Resposta do provedor inválida: ${lastError}`);

      const labelOf = new Map(subject.fields.map(f => [f.fieldPath, f.label]));
      const make = (p: Omit<ProviderFinding, "offset"> & { offset?: number }): AIReviewFinding => ({
        id: `AIF-${uuid()}`, reviewId: review.id, subjectType: subject.type, reportId: subject.reportId, revision: subject.revision,
        fieldPath: p.fieldPath, fieldLabel: labelOf.get(p.fieldPath) ?? (p.fieldPath ? p.fieldPath : "Relatório"),
        category: p.category, severity: p.severity, originalText: p.originalText, suggestedText: p.suggestedText, explanation: p.explanation,
        ruleId: p.ruleId, sourceRefs: [], autoApplicable: p.autoApplicable, justifiable: p.justifiable, offset: p.offset ?? -1,
        status: "OPEN", resolutionNote: "", resolvedBy: null, resolvedAt: null
      });
      const findings = [
        ...deterministic.map(d => make({ ...d, autoApplicable: false })),
        ...parsed.map(p => make(p))
      ].map(f => carryOver(f, previous));

      // 3. Correções linguísticas SEGURAS, se configurado e se a pessoa pode aplicar.
      let autoApplied = 0;
      if (settings.autoApplySafeLanguage && opts.canApplyLanguage && subject.editable && depth === 0) {
        autoApplied = await this.applyFindings(context, subject, findings.filter(f => f.status === "OPEN" && f.autoApplicable), "AUTO_SAFE", review.id);
      }
      await this.repo.saveFindings(findings);
      for (const f of findings) await this.record(context, AuditAction.AiFindingCreated, review, { findingId: f.id, fieldPath: f.fieldPath, category: f.category, severity: f.severity, ruleId: f.ruleId });

      if (autoApplied > 0) {
        // O texto mudou: a revisão que vale é a do conteúdo NOVO (confere se
        // a correção não introduziu problema e se as críticas continuam).
        review.status = deriveReviewStatus(findings).status;
        review.jobStatus = "COMPLETED";
        review.autoAppliedCount = autoApplied;
        Object.assign(review, countFindings(findings));
        review.completedAt = new Date().toISOString();
        await this.repo.saveReview(review);
        const next = await this.start(context, subject.type, subject.reportId, opts, depth + 1);
        const final = await next.done;
        final.autoAppliedCount = autoApplied;
        await this.repo.saveReview(final);
        return final;
      }

      const derived = deriveReviewStatus(findings);
      Object.assign(review, countFindings(findings), { status: derived.status, technicalStatus: derived.technicalStatus, jobStatus: "COMPLETED", completedAt: new Date().toISOString() });
      await this.repo.saveReview(review);
      await this.record(context, AuditAction.AiReviewCompleted, review, { status: review.status, findings: review.findingsCount, critical: review.criticalCount });
      return review;
    } catch (e) {
      review.status = "AI_REVIEW_FAILED";
      review.jobStatus = "FAILED";
      review.failureReason = e instanceof AIProviderUnavailableError ? e.message : `Serviço INSPECT IA REVIEW temporariamente indisponível. (${(e as Error)?.message || "falha"})`;
      review.completedAt = new Date().toISOString();
      await this.repo.saveReview(review);
      await this.record(context, AuditAction.AiReviewFailed, review, { reason: review.failureReason });
      return review;
    }
  }

  /** Resoluções da revisão anterior valem para o MESMO finding (mesma regra, campo e trecho). */
  private async previousFindings(review: AIReportReview): Promise<AIReviewFinding[]> {
    const list = await this.repo.listReviews({ subjectType: review.subjectType, reportId: review.reportId, limit: 5 });
    const prev = list.find(r => r.id !== review.id && r.status !== "AI_REVIEW_RUNNING" && r.status !== "AI_REVIEW_FAILED");
    return prev ? this.repo.listFindings(prev.id) : [];
  }

  /**
   * Aplica findings ao documento, UM gravação por campo, pelo serviço do
   * módulo. Guarda antes/depois de cada campo. Devolve quantos aplicou.
   */
  private async applyFindings(context: RequestContext, subject: AIReviewSubject, list: AIReviewFinding[], kind: AIAppliedChange["kind"], reviewId: string | null, manual?: Map<string, string>): Promise<number> {
    const adapter = this.adapterFor(subject.type);
    const byField = new Map<string, AIReviewFinding[]>();
    for (const f of list) byField.set(f.fieldPath, [...(byField.get(f.fieldPath) ?? []), f]);
    let applied = 0;
    const userId = String(context.userId || "system");
    for (const [fieldPath, items] of byField) {
      const field = subject.fields.find(x => x.fieldPath === fieldPath);
      if (!field || field.writable === false) continue;
      const before = field.text;
      let after = before;
      for (const f of [...items].sort((a, b) => b.offset - a.offset)) {
        const replacement = manual?.get(f.id) ?? f.suggestedText;
        const next = replaceAt(after, f.originalText, replacement, f.offset);
        if (next === after && f.originalText !== replacement) continue; // o trecho não está mais lá
        after = next;
        f.status = manual?.has(f.id) ? "EDITED" : "APPLIED";
        f.resolvedBy = userId;
        f.resolvedAt = new Date().toISOString();
        applied++;
      }
      if (after === before) continue;
      await adapter.writeField(context, subject.reportId, fieldPath, after);
      const change: AIAppliedChange = { id: `AIC-${uuid()}`, subjectType: subject.type, reportId: subject.reportId, fieldPath, findingId: items.length === 1 ? items[0]!.id : null, reviewId, kind, before, after, userId, at: new Date().toISOString() };
      await this.repo.appendChange(change);
      for (const f of items) if (f.status === "APPLIED" || f.status === "EDITED") await this.record(context, AuditAction.AiCorrectionApplied, null, { findingId: f.id, fieldPath, kind, subjectType: subject.type, reportId: subject.reportId });
    }
    return applied;
  }

  // ═══════════════════════════════════════════════════ decisões sobre findings

  private async findingAndSubject(context: RequestContext, findingId: string) {
    const finding = await this.repo.findFinding(findingId);
    if (!finding) throw new AppError(ErrorCode.NotFound, "Finding not found", { safeMessage: "Item da revisão não encontrado." });
    const latest = await this.repo.latestReview(finding.subjectType, finding.reportId);
    if (!latest || latest.id !== finding.reviewId) {
      throw new AppError(ErrorCode.Conflict, "Finding from an old review", { safeMessage: "Este item é de uma revisão anterior. Abra a revisão vigente." });
    }
    const subject = await this.loadSubject(context, finding.subjectType, finding.reportId);
    return { finding, subject, review: latest };
  }

  private assertEditable(subject: AIReviewSubject): void {
    if (!subject.editable) throw new AppError(ErrorCode.Conflict, "Document closed", { safeMessage: "Documento fechado para edição: a revisão IA é somente leitura." });
  }

  /** Aplica a sugestão de um finding e executa nova revisão. */
  async accept(context: RequestContext, findingId: string, opts: { canApplyLanguage: boolean }): Promise<AIReportReview> {
    const { finding, subject } = await this.findingAndSubject(context, findingId);
    this.assertEditable(subject);
    if (!finding.suggestedText) throw new AppError(ErrorCode.Validation, "No suggestion", { safeMessage: "Este item não tem texto sugerido — corrija no campo ou justifique." });
    if (finding.severity === "TECNICO" || finding.severity === "CRITICO") throw new AppError(ErrorCode.Validation, "Technical finding", { safeMessage: "Alerta técnico não é aplicado pela revisão: corrija o relatório ou justifique." });
    const n = await this.applyFindings(context, subject, [finding], "ACCEPTED", finding.reviewId);
    if (n === 0) throw new AppError(ErrorCode.Conflict, "Text changed", { safeMessage: "O trecho não está mais no campo (o texto mudou depois da revisão). Execute nova revisão." });
    await this.repo.saveFindings([finding]);
    return this.runReview(context, subject.type, subject.reportId, opts);
  }

  /** "APLICAR CORREÇÕES SEGURAS" — só LINGUÍSTICO auto-aplicável. Nunca técnico, conclusão ou recomendação. */
  async applySafe(context: RequestContext, type: AIReviewSubjectType, reportId: string, opts: { canApplyLanguage: boolean }): Promise<AIReportReview> {
    const subject = await this.loadSubject(context, type, reportId);
    this.assertEditable(subject);
    const review = await this.repo.latestReview(type, reportId);
    if (!review) throw new AppError(ErrorCode.Validation, "No review", { safeMessage: "Execute a revisão IA primeiro." });
    if (review.contentHash !== contentHash(subject.fields, subject.facts)) throw new AppError(ErrorCode.Conflict, "Stale review", { safeMessage: "A revisão está desatualizada: execute nova revisão antes de aplicar correções." });
    const findings = await this.repo.listFindings(review.id);
    const safe = findings.filter(f => f.status === "OPEN" && f.autoApplicable && f.severity === "LINGUISTICO");
    if (safe.length === 0) return review;
    await this.applyFindings(context, subject, safe, "ACCEPTED", review.id);
    await this.repo.saveFindings(safe);
    return this.runReview(context, type, reportId, opts);
  }

  /** "EDITAR MANUALMENTE" — o texto escrito pela pessoa entra no lugar do trecho. */
  async manualEdit(context: RequestContext, findingId: string, textValue: string, opts: { canApplyLanguage: boolean }): Promise<AIReportReview> {
    const { finding, subject } = await this.findingAndSubject(context, findingId);
    this.assertEditable(subject);
    if (typeof textValue !== "string") throw new AppError(ErrorCode.Validation, "Text required", { safeMessage: "Informe o texto." });
    // Sem trecho (ex.: incoerência): a edição substitui o campo inteiro.
    const original = finding.originalText || (subject.fields.find(f => f.fieldPath === finding.fieldPath)?.text ?? "");
    const target = { ...finding, originalText: original, offset: finding.originalText ? finding.offset : 0 };
    const n = await this.applyFindings(context, subject, [target], "MANUAL_EDIT", finding.reviewId, new Map([[finding.id, textValue]]));
    if (n === 0) throw new AppError(ErrorCode.Conflict, "Text changed", { safeMessage: "O trecho não está mais no campo. Execute nova revisão." });
    await this.repo.saveFindings([{ ...finding, status: "EDITED", resolutionNote: "Texto editado manualmente.", resolvedBy: String(context.userId || ""), resolvedAt: new Date().toISOString() }]);
    return this.runReview(context, subject.type, subject.reportId, opts);
  }

  async reject(context: RequestContext, findingId: string, reason: string): Promise<AIReviewFinding> {
    const { finding, review } = await this.findingAndSubject(context, findingId);
    if (finding.severity === "TECNICO" || finding.severity === "CRITICO") throw new AppError(ErrorCode.Validation, "Technical finding", { safeMessage: "Alerta técnico não é rejeitado: corrija o relatório ou justifique." });
    return this.resolve(context, review, finding, "REJECTED", (reason || "").trim(), AuditAction.AiCorrectionRejected);
  }

  /** Aceitar o alerta técnico = reconhecer que procede. Continua exigindo CORREÇÃO. */
  async acknowledge(context: RequestContext, findingId: string): Promise<AIReviewFinding> {
    const { finding, review } = await this.findingAndSubject(context, findingId);
    return this.resolve(context, review, finding, "ACKNOWLEDGED", "Alerta reconhecido — relatório a corrigir.", AuditAction.AiTechnicalWarningAccepted);
  }

  async justify(context: RequestContext, findingId: string, justification: string): Promise<AIReviewFinding> {
    const { finding, review } = await this.findingAndSubject(context, findingId);
    if (!finding.justifiable) throw new AppError(ErrorCode.Validation, "Not justifiable", { safeMessage: finding.severity === "CRITICO" ? "Inconsistência CRÍTICA não é justificável: corrija o relatório." : "Este item não admite justificativa: corrija o relatório." });
    const text = (justification || "").trim();
    if (text.length < 15) throw new AppError(ErrorCode.Validation, "Justification required", { safeMessage: "Escreva a justificativa técnica (pelo menos 15 caracteres)." });
    return this.resolve(context, review, finding, "JUSTIFIED", text, AuditAction.AiTechnicalWarningJustified);
  }

  private async resolve(context: RequestContext, review: AIReportReview, finding: AIReviewFinding, status: AIReviewFinding["status"], note: string, action: AuditAction): Promise<AIReviewFinding> {
    const updated: AIReviewFinding = { ...finding, status, resolutionNote: note, resolvedBy: String(context.userId || ""), resolvedAt: new Date().toISOString() };
    await this.repo.saveFindings([updated]);
    const all = await this.repo.listFindings(review.id);
    const derived = deriveReviewStatus(all);
    Object.assign(review, countFindings(all), { status: derived.status, technicalStatus: derived.technicalStatus });
    await this.repo.saveReview(review);
    await this.record(context, action, review, { findingId: finding.id, fieldPath: finding.fieldPath, severity: finding.severity });
    return updated;
  }

  /** Desfaz uma alteração aplicada — só se o campo ainda estiver como a alteração deixou. */
  async revertChange(context: RequestContext, changeId: string, type: AIReviewSubjectType, reportId: string): Promise<void> {
    const change = (await this.repo.listChanges({ subjectType: type, reportId })).find(c => c.id === changeId);
    if (!change) throw new AppError(ErrorCode.NotFound, "Change not found", { safeMessage: "Alteração não encontrada." });
    const subject = await this.loadSubject(context, type, reportId);
    this.assertEditable(subject);
    const current = subject.fields.find(f => f.fieldPath === change.fieldPath)?.text;
    if (current !== change.after) throw new AppError(ErrorCode.Conflict, "Field changed", { safeMessage: "O campo mudou depois dessa alteração: desfazer apagaria o que veio depois." });
    await this.adapterFor(type).writeField(context, reportId, change.fieldPath, change.before);
    await this.repo.appendChange({ ...change, id: `AIC-${uuid()}`, kind: "REVERT", before: change.after, after: change.before, userId: String(context.userId || "system"), at: new Date().toISOString() });
  }

  /** Parecer consultivo do envio ao PLH — registra auditoria se aplicável, sem bloquear o fluxo. */
  async assertReadyForSubmit(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<void> {
    if (!this.adapters.has(type)) return;
    try {
      const s = await this.state(context, type, reportId);
      if (s.review?.status === "AI_REVIEW_FAILED") {
        await this.record(context, AuditAction.AiSubmittedWithoutReview, s.review, { reason: s.review.failureReason });
      }
    } catch {
      // Falhas na consulta consultiva da IA nunca devem impedir o workflow humano
    }
  }

  private async record(context: RequestContext, action: AuditAction, review: AIReportReview | null, metadata: Record<string, unknown>): Promise<void> {
    // Só metadados — o conteúdo do relatório não vai para o log de auditoria.
    await this.audit?.record(context, {
      action, entityType: "AIReportReview", entityId: review?.id ?? String(metadata["reportId"] ?? ""), previousState: null, newState: null,
      metadata: { ...(review ? { subjectType: review.subjectType, reportId: review.reportId } : {}), ...metadata } as never
    });
  }
}

function carryOver(f: AIReviewFinding, previous: readonly AIReviewFinding[]): AIReviewFinding {
  const same = previous.find(p => p.ruleId === f.ruleId && p.fieldPath === f.fieldPath && p.originalText === f.originalText && p.severity === f.severity && ["REJECTED", "JUSTIFIED", "ACKNOWLEDGED"].includes(p.status));
  return same ? { ...f, status: same.status, resolutionNote: `${same.resolutionNote} (mantida da revisão anterior)`.trim(), resolvedBy: same.resolvedBy, resolvedAt: same.resolvedAt } : f;
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([p, new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new AIProviderUnavailableError("Tempo limite do provedor esgotado.")), ms); })]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
